/**
 * @fileoverview What the gardener meets when something they tried needs Grow More (the
 * paid plan; Grow is the app), and the line in the header that says where
 * they stand: the trial's days
 * left (or, before their first plant, that the trial starts with it), Free,
 * a founding gardener's free period, or a paused plan.
 *
 * The API client fires one event when the backend answers 402. Wherever
 * they were, this dialog says why and offers the subscribe page; what they
 * tried is simply not done, and nothing else changes. Its reasons: a paused
 * plan (until when, with Resume); Free's plants all there ("Free is up to 5
 * plants": the refusal's limit, else the server's access.limits.plants.limit,
 * while that many or more are growing); or the photo
 * AI (photo identification, seed-packet scans, plant check-ups), which Free
 * and a paused plan do not have. A trial that ends goes onto Free, never
 * read-only, so nothing here says "trial ended". A member tending someone
 * else's garden (useTendingFor; B23, owner 2026-10-20: the invitee only uses
 * the inviter's plan) is told why with the server's tending line in place of
 * the body, and offered nothing to buy: Close only, no "See plans" or "Resume".
 *
 * Where: SubscriptionWall is mounted once in the Layout; TrialLine sits in
 * the Header (compact on phones).
 * API: none of its own; it listens for SUBSCRIPTION_REQUIRED_EVENT, which
 * the API client (shared/api/apiClient) fires on any 402, and reads GET
 * /billing/status (useBilling).
 * A paused plan's day billing starts again is the server's status line
 * (access.statusLine, kind 'paused' with its day); a pause with no end is
 * kind 'pausedOpen' and names no day. The app never reads a year to tell.
 * In someone else's garden (the status's tendingFor, B23) the line is the
 * garden owner's plan, as the server sends it, and offers nothing to buy.
 * Foot (B111): ActionRow, "Not now" left (outline) and the link to /subscribe right; Close alone in someone else's garden.
 * Words (en/es): billing.wall.*, billing.line.*, sitewide.close; the tending line is the server's (todo_word billing.tending.line).
 * Watch out: the 402 that opened the wall is already a failed request; the
 * wall only explains it, it does not retry what was tried.
 *
 * @module SubscriptionWall
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { Access } from '@/shared/api/billing';
import { SUBSCRIPTION_REQUIRED_EVENT } from '@/shared/lib/subscriptionEvent';
import { cn } from '@/lib/utils';
import { useBilling, useTendingFor } from '../lib/useBilling';
import { pauseDay } from '../lib/pauseDay';
import { foundingDay } from '../lib/foundingDay';

/**
 * A paused plan's day billing starts again, as the gardener's own day ("February 17"): the server's status
 * line's day; null for a pause with no end (kind pausedOpen), or when the server says none.
 *
 * @param access - The billing status's access.
 * @param language - The app's language.
 */
function pausedUntilDay(access: Access | undefined, language: string): string | null {
  const line = access?.statusLine;
  return line?.kind === 'paused' ? pauseDay(line.until, language) || null : null;
}

/**
 * The look every header pill link shares: a bordered round pill, never underlined
 * in any state, a soft hover fill and a visible keyboard focus ring.
 */
const PILL_LINK = 'no-underline hover:no-underline focus:no-underline inline-flex rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The founding gardener's and a gift's status: the same words, as plain small
 * text with no pill around them (owner, 2026-10-13: "its the look with the
 * round pill. its so ugly but i dont mind the text as is").
 */
const STATUS_LINK = 'no-underline hover:no-underline focus:no-underline inline-flex rounded-sm text-xs text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** What brought the dialog up, as the API client's event says it. */
interface WallDetail {
  /** The photo AI was refused. */
  ai?: boolean;
  /** 'plants': Free's plants are all growing (FREE_PLANT_LIMIT). */
  kind?: 'plants';
  /** Up to how many plants Free has, with kind 'plants'. */
  limit?: number;
}

/** The dialog that answers a 402 from anywhere: the plan is paused, Free's plants are all there, or the model is Grow More's; with the way on (none in someone else's garden). */
export const SubscriptionWall: React.FC = () => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { data } = useBilling();
  /** Someone else's garden: the owner's plan, so nothing here is offered to buy (B23). */
  const tendingFor = useTendingFor();
  const [open, setOpen] = React.useState(false);
  /** Which refusal brought this up. */
  const [detail, setDetail] = React.useState<WallDetail>({});

  React.useEffect(() => {
    /** A refusal heard: open, worded for whichever it was. */
    const show = (event: Event): void => {
      setDetail((event as CustomEvent<WallDetail | undefined>).detail ?? {});
      setOpen(true);
    };
    window.addEventListener(SUBSCRIPTION_REQUIRED_EVENT, show);
    return () => window.removeEventListener(SUBSCRIPTION_REQUIRED_EVENT, show);
  }, []);

  // Paused: the same wall, its own words (the plan is paused, not ended).
  const paused = data?.access.paused === true;
  const until = paused ? pausedUntilDay(data?.access, language) : null;
  // The refusal's own limit, else the server's; the plants refusal always carries one.
  const limit = detail.limit ?? data?.access.limits?.plants.limit ?? '';
  let title: string;
  let body: string;
  if (paused) {
    title = t('billing.wall.pausedTitle');
    body = until ? t('billing.wall.pausedBody', { date: until }) : t('billing.wall.pausedBodyOpen');
  } else if (detail.kind === 'plants') {
    title = t('billing.wall.plantsTitle', { limit });
    body = t('billing.wall.plantsBody');
  } else if (detail.ai) {
    title = t('billing.wall.aiTitle');
    body = t('billing.wall.aiBody');
  } else {
    title = t('billing.wall.title');
    body = t('billing.wall.body');
  }
  // In someone else's garden the plan is the owner's: say so, in the server's words, in place of the way to subscribe.
  if (tendingFor) body = tendingFor.line;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {tendingFor ? (
            // Nothing to buy or resume in someone else's garden: only a way out.
            <Button onClick={() => setOpen(false)}>{t('sitewide.close')}</Button>
          ) : (
            <ActionRow onCancel={() => setOpen(false)} cancelLabel={t('billing.wall.later')}>
              <Button asChild onClick={() => setOpen(false)}>
                <Link to="/subscribe">{paused ? t('billing.wall.resume') : t('billing.wall.subscribe')}</Link>
              </Button>
            </ActionRow>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * "12 days of Grow More left · Subscribe", shown in the header during the
 * trial; before the first plant, "14 days of Grow More start with your first
 * plant · Subscribe" (the
 * trial from the first plant waits for it); on Free (a trial or subscription
 * that ended), "Free · Subscribe", calmly. Nothing while subscribed; "Paused
 * until February 17" while a monthly plan is paused, linking to the
 * subscribe page (where it can be resumed). Compact (a phone's header row):
 * the days alone ("12 days left"; "14-day trial" while it waits), the whole
 * of it still the link to subscribe. The long "12 days of Grow More left" made
 * the phone's header row 412px wide at 375, so the whole page scrolled sideways.
 *
 * A founding gardener in their free period has no countdown: "Founding
 * gardener · free until Feb 28", a link to the subscribe page like the other
 * pills (where it says when the free period ends and what follows). Compact,
 * the same words on two lines, as plain text with no pill (STATUS_LINK). No status is underlined in any state.
 *
 * A gardener whose trial is a gift of a year has no countdown either: "Gift
 * · until Dec 25, 2027", linking to the subscribe page (where
 * they can subscribe to keep it after). Compact, on two lines.
 *
 * A member tending someone else's garden (the status's tendingFor; B23, owner
 * 2026-10-20) is shown that owner's plan, which the server sends in place of
 * their own: the owner's days, Free, pause, founding or gift, as above, and
 * no "Subscribe" (the subscribe page there says whose plan it is, with no
 * buttons).
 */
export const TrialLine: React.FC<{ className?: string; compact?: boolean }> = ({ className, compact = false }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { data } = useBilling();
  const tendingFor = useTendingFor();
  if (!data) return null;
  // The server's line says which of the plan's states this is (C118: the app compares no state of its own).
  const line = data.access.statusLine;
  if (line.kind === 'active' || line.kind === 'ending' || line.kind === 'paymentProblem' || line.kind === 'pauseAhead') return null;
  if (line.kind === 'paused' || line.kind === 'pausedOpen') {
    const until = pausedUntilDay(data.access, language);
    return (
      <Link
        to="/subscribe"
        className={cn(PILL_LINK, 'items-center', className)}
      >
        {/* Compact (a phone's header row): the word alone. */}
        {until && !compact ? t('billing.line.paused', { date: until }) : t('billing.line.pausedOpen')}
      </Link>
    );
  }
  if (line.kind === 'founding') {
    // The last free day is a day, not a moment: read as the gardener's own date, with its year.
    const until = foundingDay(line.date, language) || null;
    return (
      <Link
        to="/subscribe"
        className={cn(
          STATUS_LINK,
          // Two short lines, small enough to leave the phone's header row its avatar at 360px.
          compact ? 'flex-col items-start text-[11px] leading-tight' : 'items-center gap-1.5',
          className,
        )}
      >
        <span className="font-semibold text-foreground">{t('billing.line.founding')}</span>
        {until && <span>{compact ? t('billing.line.foundingUntilShort', { date: until }) : t('billing.line.foundingUntil', { date: until })}</span>}
      </Link>
    );
  }
  if (line.kind === 'gift') {
    // A gift of a year: no countdown, its last day (a day, read as theirs), and the way to keep it after.
    const day = line.date ? DateTime.fromISO(line.date) : null;
    const until = day?.isValid ? day.setLocale(language).toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' }) : null;
    return (
      <Link
        to="/subscribe"
        className={cn(
          STATUS_LINK,
          compact ? 'flex-col items-start text-[11px] leading-tight' : 'items-center gap-1.5',
          className,
        )}
      >
        <span className="font-semibold text-foreground">{t('billing.line.gift')}</span>
        {until && <span>{compact ? t('billing.line.giftUntilShort', { date: until }) : t('billing.line.giftUntil', { date: until })}</span>}
      </Link>
    );
  }
  // On Free: the plan's name, not a warning. Otherwise the trial, in the server's kind and days.
  const days = line.days ?? 0;
  let said: string;
  if (line.kind === 'free') said = t('billing.line.free');
  else if (line.kind === 'trialWaiting') said = compact ? t('billing.line.trialWaitingShort', { days }) : t('billing.line.trialWaiting', { days });
  else if (compact) said = line.kind === 'trialOne' ? t('billing.line.trialOneShort') : t('billing.line.trialShort', { days });
  else said = line.kind === 'trialOne' ? t('billing.line.trialOne') : t('billing.line.trial', { days });
  return (
    <Link
      to="/subscribe"
      className={cn(
        PILL_LINK, 'items-center gap-1.5 whitespace-nowrap',
        className,
      )}
    >
      <span>{said}</span>
      {/* Nothing to buy in someone else's garden: the plan is the owner's. */}
      {!compact && !tendingFor && <span className="font-medium">{t('billing.line.subscribe')}</span>}
    </Link>
  );
};
